import { useMemo, useState, type FormEvent } from 'react';
import { ShopError, api } from '../api';
import { readConsent } from '../tracking/consent';
import { getVisitorId } from '../tracking/ids';
import { ButtonBlock } from './blocks/ButtonBlock';
import { CountdownBlock } from './blocks/CountdownBlock';
import { DiscountCodeBlock } from './blocks/DiscountCodeBlock';
import { FormBlock, type FieldValue } from './blocks/FormBlock';
import { ImageBlock } from './blocks/ImageBlock';
import { ProductsBlock } from './blocks/ProductsBlock';
import { TextBlock } from './blocks/TextBlock';
import { evergreenEndsAt } from './store';
import type { LayerBlock, LayerDelivery } from './types';

/**
 * Vykreslí obsah jedné vrstvy: bloky `doc.blocks` v režimu formuláře, po
 * úspěšném odeslání přepne na `doc.success`.
 *
 * FORMULÁŘ: e-mail jde VÝHRADNĚ přes `api.subscribeNewsletter` (jediná
 * zápisová cesta odběratele, `source='popup'` + `layerId` pro atribuci).
 * Teprve po jeho úspěchu se ne-e-mailová pole pošlou jako atributy
 * (`api.submitLayerForm`); jejich chyba se tiše loguje — odběr už platí.
 */
export function LayerRenderer({
  layer,
  onClose,
  onInteract,
  onSubmitted,
}: {
  layer: LayerDelivery;
  onClose: () => void;
  onInteract: (kind: 'click' | 'submit') => void;
  onSubmitted: () => void;
}) {
  const [mode, setMode] = useState<'form' | 'success'>('form');
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'error'>('idle');
  const [error, setError] = useState('');

  const doc = layer.doc;
  const blocks = mode === 'success' ? doc.success : doc.blocks;

  // Mapa id → slevový kód (pro tlačítko copy_code).
  const codeByBlockId = useMemo(() => {
    const map = new Map<string, string>();
    for (const block of [...doc.blocks, ...doc.success]) {
      if (block.type === 'discount_code') map.set(block.id, block.code);
    }
    return map;
  }, [doc]);

  const formBlock = useMemo(
    () => doc.blocks.find((block): block is Extract<LayerBlock, { type: 'form' }> => block.type === 'form'),
    [doc],
  );
  const hasForm = formBlock !== undefined;

  function setValue(name: string, value: FieldValue): void {
    setValues((current) => ({ ...current, [name]: value }));
    if (status === 'error') setStatus('idle');
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (status === 'sending' || formBlock === undefined) return;

    const emailField = formBlock.fields.find((field) => field.type === 'email');
    const email = emailField !== undefined ? String(values[emailField.name] ?? '').trim() : '';

    if (emailField !== undefined && email === '') {
      setStatus('error');
      setError('Zadej prosím e-mail.');
      return;
    }

    setStatus('sending');
    const consent = readConsent();
    try {
      if (emailField !== undefined) {
        await api.subscribeNewsletter(email, consent.analytics ? getVisitorId() : undefined, {
          source: 'popup',
          layerId: layer.id,
        });
      }

      // Ne-e-mailová pole → atributy. Chyba tiše (odběr už platí).
      const attributes: Record<string, unknown> = {};
      for (const field of formBlock.fields) {
        if (field.type === 'email') continue;
        if (field.attribute === undefined) continue;
        const raw = values[field.name];
        if (raw !== undefined && raw !== '') attributes[field.attribute] = raw;
      }
      if (Object.keys(attributes).length > 0) {
        try {
          await api.submitLayerForm(layer.id, email, attributes);
        } catch {
          // Atributy nejsou kritické — odběr proběhl.
        }
      }

      onInteract('submit');
      onSubmitted();
      setStatus('idle');
      setMode('success');
    } catch (err) {
      setStatus('error');
      setError(
        err instanceof ShopError ? err.message : 'Nepodařilo se odeslat. Zkus to za chvíli.',
      );
    }
  }

  function copyCode(blockId: string): void {
    onInteract('click');
    const code = codeByBlockId.get(blockId);
    if (code !== undefined) void navigator.clipboard?.writeText(code).catch(() => undefined);
  }

  function renderBlock(block: LayerBlock) {
    switch (block.type) {
      case 'text':
        return <TextBlock key={block.id} block={block} />;
      case 'image':
        return <ImageBlock key={block.id} block={block} />;
      case 'form':
        return (
          <FormBlock
            key={block.id}
            block={block}
            values={values}
            setValue={setValue}
            status={status}
            errorMessage={error}
          />
        );
      case 'button':
        return (
          <ButtonBlock
            key={block.id}
            block={block}
            onSubmit={() => onInteract('click')}
            onClose={onClose}
            onLink={() => onInteract('click')}
            onCopyCode={copyCode}
          />
        );
      case 'countdown':
        return (
          <CountdownBlock
            key={block.id}
            block={block}
            evergreenEndsAt={evergreenEndsAt(layer.id)}
            onExpireHide={onClose}
          />
        );
      case 'discount_code':
        return (
          <DiscountCodeBlock key={block.id} block={block} onCopy={() => onInteract('click')} />
        );
      case 'products':
        return <ProductsBlock key={block.id} block={block} />;
      default:
        return null;
    }
  }

  const content = blocks.map(renderBlock);
  const dismissLabel = doc.settings.dismissLabel;

  if (mode === 'form' && hasForm) {
    return (
      <form className="layer-content" onSubmit={handleSubmit} noValidate={false}>
        {content}
        {dismissLabel !== undefined && dismissLabel !== '' && (
          <button type="button" className="layer-dismiss" onClick={onClose}>
            {dismissLabel}
          </button>
        )}
      </form>
    );
  }

  return <div className="layer-content">{content}</div>;
}
